import { useRef, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Stack,
    Paper,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Chip from '@mui/material/Chip';
import { uploadPreviousInvestigation, downloadPreviousInvestigationUrl } from '../../../../api/encounter';
import SectionHeading from './SectionHeading';

const NAVY = '#052F5D';
const MAX_BYTES = 20 * 1024 * 1024;

function humanSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function PreviousInvestigationsTab({ encounter, onReload, onPatch, disabled }) {
    const fileInput = useRef(null);
    const [pending, setPending] = useState(null); // { file, label, preview }
    const [progress, setProgress] = useState(null); // 0..100
    const [error, setError] = useState(null);

    const items = encounter?.previousInvestigations || [];
    const none = Boolean(encounter?.previousInvestigationsNone);

    const onPickFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > MAX_BYTES) {
            setError(`File too large (${humanSize(file.size)}). Maximum is ${humanSize(MAX_BYTES)}.`);
            return;
        }
        setError(null);
        const isImage = file.type.startsWith('image/');
        const preview = isImage ? URL.createObjectURL(file) : null;
        setPending({ file, label: file.name.replace(/\.[^.]+$/, ''), preview });
    };

    const cancelPending = () => {
        if (pending?.preview) URL.revokeObjectURL(pending.preview);
        setPending(null);
        setProgress(null);
        setError(null);
    };

    const confirmUpload = async () => {
        if (!pending) return;
        setProgress(5);
        // We don't have axios's onUploadProgress here (using fetch). Fake a smooth
        // climb so users get visual reassurance, then snap to 100 on resolve.
        const tick = setInterval(() => {
            setProgress((p) => (p === null ? p : Math.min(90, p + 5)));
        }, 120);
        try {
            await uploadPreviousInvestigation(encounter._id, pending.file, { label: pending.label });
            clearInterval(tick);
            setProgress(100);
            await onReload?.();
            // An uploaded result supersedes any "no previous investigations"
            // assertion — clear it so the two never coexist.
            if (none) onPatch?.({ previousInvestigationsNone: false });
            setTimeout(cancelPending, 250);
        } catch (err) {
            clearInterval(tick);
            setProgress(null);
            setError(err?.message || 'Upload failed.');
        }
    };

    return (
        <Box>
            <SectionHeading
                title="Previous investigations"
                hint="Lab results, imaging reports, or other documents the patient brought in. If they brought none, mark it so the visit summary records that."
            />

            {!disabled && items.length === 0 && (
                <Box sx={{ mb: 2 }}>
                    <Chip
                        clickable
                        onClick={() => onPatch?.({ previousInvestigationsNone: !none })}
                        icon={none ? <CheckRoundedIcon /> : undefined}
                        label="No previous investigations or documents"
                        variant={none ? 'filled' : 'outlined'}
                        sx={none ? {
                            bgcolor: 'rgba(46,125,50,0.12)',
                            color: 'success.dark',
                            border: '1px solid rgba(46,125,50,0.4)',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'success.dark' },
                        } : {
                            borderColor: 'rgba(5,47,93,0.30)',
                            color: NAVY,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            )}

            {!disabled && (
                <Paper
                    variant="outlined"
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer.files?.[0];
                        if (f) onPickFile({ target: { files: [f], value: '' } });
                    }}
                    sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: 2,
                        borderStyle: 'dashed',
                        borderColor: 'rgba(5,47,93,0.25)',
                        bgcolor: 'rgba(5,47,93,0.02)',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease, border-color 150ms ease',
                        '&:hover': { bgcolor: 'rgba(5,47,93,0.04)', borderColor: NAVY },
                    }}
                    onClick={() => fileInput.current?.click()}
                >
                    <input
                        ref={fileInput}
                        type="file"
                        hidden
                        accept="image/*,application/pdf,.doc,.docx"
                        onChange={onPickFile}
                    />
                    <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.6 }} />
                    <Typography sx={{ mt: 1, fontWeight: 600, color: NAVY }}>
                        Drag a file here, or click to browse
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        PDF, image, or document up to 20 MB. You'll review before uploading.
                    </Typography>
                </Paper>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {items.length === 0 ? (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        borderRadius: 1.5,
                        bgcolor: none ? 'rgba(46,125,50,0.06)' : 'rgba(5,47,93,0.025)',
                        border: `1px dashed ${none ? 'rgba(46,125,50,0.35)' : 'rgba(5,47,93,0.15)'}`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" sx={{ color: none ? 'success.dark' : 'text.secondary' }}>
                        {none
                            ? 'Recorded: no previous investigations or documents. This will appear on the visit summary.'
                            : 'No documents uploaded yet.'}
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {items.map((it) => (
                        <Paper
                            key={it._id}
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: 1.5, borderColor: 'rgba(5,47,93,0.10)', display: 'flex', alignItems: 'center', gap: 1.5 }}
                        >
                            <InsertDriveFileOutlinedIcon sx={{ color: NAVY }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, color: NAVY, lineHeight: 1.25 }}>
                                    {it.label || it.originalName || 'Document'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {it.originalName || ''}{' '}
                                    {it.sizeBytes ? `· ${humanSize(it.sizeBytes)}` : ''}{' '}
                                    {it.uploadedAt ? `· ${new Date(it.uploadedAt).toLocaleString()}` : ''}
                                </Typography>
                            </Box>
                            <Tooltip title="Open" arrow>
                                <IconButton
                                    component="a"
                                    href={downloadPreviousInvestigationUrl(encounter._id, it._id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    size="small"
                                    sx={{ color: NAVY }}
                                >
                                    <OpenInNewRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    ))}
                </Stack>
            )}

            <Dialog open={Boolean(pending)} onClose={progress !== null ? undefined : cancelPending} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: NAVY, fontWeight: 600 }}>Upload document</DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    {pending && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                {pending.preview ? (
                                    <Box
                                        component="img"
                                        src={pending.preview}
                                        alt="Preview"
                                        sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1.5, border: '1px solid rgba(5,47,93,0.1)' }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 96, height: 96, borderRadius: 1.5,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: 'rgba(5,47,93,0.04)', border: '1px solid rgba(5,47,93,0.1)',
                                        }}
                                    >
                                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: NAVY, opacity: 0.7 }} />
                                    </Box>
                                )}
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>{pending.file.name}</Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {pending.file.type || 'unknown type'} · {humanSize(pending.file.size)}
                                    </Typography>
                                </Box>
                            </Box>
                            <TextField
                                label="Label"
                                fullWidth
                                size="small"
                                value={pending.label}
                                onChange={(e) => setPending({ ...pending, label: e.target.value })}
                                disabled={progress !== null}
                                helperText="Shown on the encounter and in the summary PDF."
                            />
                            {progress !== null && (
                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progress}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(5,47,93,0.08)',
                                            '& .MuiLinearProgress-bar': { bgcolor: NAVY },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                        Uploading… {progress}%
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={cancelPending} disabled={progress !== null && progress < 100} sx={{ color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmUpload}
                        disabled={progress !== null || !pending?.label?.trim()}
                        variant="contained"
                        startIcon={<CloudUploadOutlinedIcon />}
                        sx={{
                            bgcolor: NAVY,
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#03244a', boxShadow: 'none' },
                        }}
                    >
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
